/**
 * CSR Sales Log — pt.93 (2026-09-22).
 *
 * 6-stage workflow matching Katt's spreadsheet. Stages 1|2|3 are
 * user-editable (schedule state). Stages 4|5|6 are compute-owned —
 * they flip automatically on payroll auto-match, run approval, and
 * AR-clear (via the run's `st_ar_open` snapshot, per Brett's Q2.c:
 * AR-clear routes through the approved compute path, never a direct
 * stage-6 bypass).
 *
 * Extra vs pt.91: CSV backfill UI to import historical rows from
 * the spreadsheet.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Search, PlusCircle, AlertCircle, CheckCircle2,
  Send, Trash2, RefreshCw, Sparkles, Upload, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MarkPaidModal, PaidBadge, undoPaid } from "@/components/csr/MarkPaidPanel";
import { ApprovedPoolCatchup } from "@/components/csr/ApprovedPoolCatchup";

const STATUS_ORDER = [
  "open_need_to_schedule",
  "closing_this_week",
  "closing_next_week",
  "needs_payout",
  "needs_pay_to_collect",
  "completed_collected",
];

const STATUS_LABEL = {
  open_need_to_schedule: "Open — need to schedule",
  closing_this_week: "Closing this week",
  closing_next_week: "Closing next week",
  needs_payout: "Needs payout",
  needs_pay_to_collect: "Needs pay to collect",
  completed_collected: "Completed / collected",
};

const STATUS_SHORT = {
  open_need_to_schedule: "Open",
  closing_this_week: "Closing this wk",
  closing_next_week: "Closing next wk",
  needs_payout: "Needs payout",
  needs_pay_to_collect: "Pay-to-collect",
  completed_collected: "Completed",
};

const STATUS_STYLE = {
  open_need_to_schedule: "bg-[#f4f2ef] text-[#5a4d3a]",
  closing_this_week: "bg-[#fef3d9] text-[#8a5a1a]",
  closing_next_week: "bg-[#fde9d0] text-[#a35418]",
  needs_payout: "bg-[#e0f2ff] text-[#005a94]",
  needs_pay_to_collect: "bg-[#ecdcfb] text-[#5a2ea6]",
  completed_collected: "bg-[#e0f4ec] text-[#0e6b52]",
};

const MANUAL_STATES = new Set([
  "open_need_to_schedule",
  "closing_this_week",
  "closing_next_week",
]);

// CC-066 (2026-09-25): the TRUE lock condition is "matched to a real payroll
// run", not "status label is a compute-owned enum". The 48 historical
// backfill rows Katt imported carry `completed_collected` / `needs_pay_to_collect`
// status strings but have never been bound to a run (`matched_run_id` +
// `matched_pay_week_monday` are both null) — those rows must remain
// user-editable. Only rows genuinely bound to an approved payroll run are
// compute-owned and locked. Keep the check on both fields so future
// server-side changes that only set one field still lock correctly.
const isRowLocked = (r) =>
  Boolean(r?.matched_run_id) || Boolean(r?.matched_pay_week_monday);

// CC-062 — pay period for a matched sale: matched Monday through the following Sunday.
const payPeriod = (monday) => {
  if (!monday) return null;
  const end = new Date(`${monday}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${monday} to ${end.toISOString().slice(0, 10)}`;
};

function LockedSaleBanner({ sale }) {
  const period = payPeriod(sale.matched_pay_week_monday);
  return (
    <div
      role="alert"
      data-testid="csr-sales-form-locked-banner"
      className="w-full rounded-lg border-2 border-[#d9a441] bg-[#fef3d9] px-4 py-3 text-[13.5px] leading-relaxed text-[#5a3a0a]"
    >
      This sale is at stage {STATUS_ORDER.indexOf(sale.status) + 1 || "?"} ({STATUS_LABEL[sale.status] || sale.status}).
      {" "}Its numbers are computed from a paid payroll run and can't be edited here.
      {" "}If a change is needed, contact a payroll admin (Missie or Nathanael) and mention the pay period
      {" "}<span className="font-mono-data font-semibold" data-testid="csr-sales-form-locked-pay-period">{period || "shown on this sale"}</span>.
    </div>
  );
}

const money = (v) =>
  v == null || v === ""
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CsrSalesLog() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [csrProfiles, setCsrProfiles] = useState([]);
  const [tab, setTab] = useState("open_need_to_schedule");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [markPaidJobs, setMarkPaidJobs] = useState(null); // CC-225
  const [editing, setEditing] = useState(null);

  // Role gates — matches backend Q1(b) + Q2(b).
  const isCsrOnly = user?.role === "call_center";
  const canImportCsv = !isCsrOnly;  // Q2(b): payroll ops only
  // CC-110 Deploy 1a — legacy AR-clear unwind. Platform_admin only,
  // mirrors backend gate in csr_sales.py unwind_legacy_ar_clears.
  const canUnwindLegacyArClears = user?.role === "platform_admin";
  const [showUnwindModal, setShowUnwindModal] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const { data: sales } = await api.get(`/csr-sales?tenant_slug=${slug}`);
      setRows(sales.rows || []);
      setCounts(sales.counts || {});
      // csr_profiles is only used for the "pick a CSR" dropdown on the
      // form — non-critical for call_center (they auto-resolve to their
      // own). Fail-soft so a 403 on that endpoint doesn't blank the page.
      try {
        const { data: profiles } = await api.get(`/csr-profiles?tenant_slug=${slug}`);
        setCsrProfiles(Array.isArray(profiles) ? profiles : []);
      } catch {
        setCsrProfiles([]);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(
    () => rows.filter((r) => r.status === tab),
    [rows, tab],
  );

  return (
    <div className="space-y-5" data-testid="csr-sales-log-page">
      <header>
        {/* CC-063 (2026-09-25) — Back-link points at the Sales Log's
            own list URL. Feels self-referential when the user is
            already on `/payroll/csr-sales`, but the intent is a
            "reset to base list" affordance: it strips any lingering
            tab/filter query params from the URL and returns the
            page to its default state without a full reload. Ships
            per Brett's CC-063 spec — do not repoint at /overview.
            CC-061a v2 rewrote the eyebrow "Payroll · CSR Sales Log"
            → "CSR Sales Log"; that half stays. */}
        <Link
          to={`/${slug}/app/payroll/csr-sales`}
          className="inline-flex items-center gap-1 text-[12px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420] mb-1"
          data-testid="csr-sales-back-link"
        >
          <ArrowLeft size={12} /> Back to CSR Sales Log
        </Link>
        <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">
          CSR Sales Log
        </div>
        <div className="flex items-start justify-between gap-4 mt-1">
          <div>
            <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420]">
              CSR Sales Log
            </h1>
            <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1 max-w-[820px]">
              Six-stage workflow matching your spreadsheet. Stages 1–3 (schedule
              state) are editable. Stages 4–6 flip automatically on payroll
              auto-match, run approval, and AR-clear.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={busy}
              data-testid="csr-sales-refresh-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f9fafb] disabled:opacity-60"
            >
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh
            </button>
            {canImportCsv && (
              <button type="button" onClick={() => setMarkPaidJobs("")} data-testid="csr-sales-mark-paid-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] text-[#14532d] hover:bg-[#f6faf7]">
                Mark paid by job #
              </button>
            )}
            {canImportCsv && (
              <button
                type="button"
                onClick={() => setShowImport(true)}
                data-testid="csr-sales-import-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f9fafb]"
              >
                <Upload size={13} /> Import CSV
              </button>
            )}
            <button
              type="button"
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="csr-sales-new-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a94]"
            >
              <PlusCircle size={13} /> Log new sale
            </button>
          </div>
        </div>
      </header>

      {["owner", "platform_admin"].includes(user?.role) && <ApprovedPoolCatchup slug={slug} onApplied={load} />}

      {err && (
        <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c] flex items-start gap-2" data-testid="csr-sales-err">
          <AlertCircle size={14} strokeWidth={1.9} className="mt-0.5" />
          <div>{typeof err === "string" ? err : JSON.stringify(err)}</div>
        </div>
      )}

      {showForm && (
        <SaleForm
          slug={slug}
          user={user}
          editing={editing}
          csrProfiles={csrProfiles}
          isCsrOnly={isCsrOnly}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {markPaidJobs !== null && (
        <MarkPaidModal slug={slug} initialJobs={markPaidJobs} onClose={() => setMarkPaidJobs(null)} onDone={load} />
      )}

      {showImport && (
        <ImportModal
          slug={slug}
          csrProfiles={csrProfiles}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); load(); }}
        />
      )}

      <div className="flex flex-wrap gap-1 border-b border-[#e7e9ee]" data-testid="csr-sales-tabs">
        {STATUS_ORDER.map((t, i) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            data-testid={`csr-sales-tab-${t}`}
            className={`px-3.5 py-2 text-[12px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === t
                ? "border-[#0072bc] text-[#005a94]"
                : "border-transparent text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
            }`}
          >
            <span className="text-[10px] text-[color:var(--cc-ink-muted)] mr-1">{i + 1}.</span>
            {STATUS_SHORT[t]}
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] px-1.5 h-[16px] text-[10px] font-bold rounded-full bg-[#eef1f6] text-[#0e1420]">
              {counts[t] || 0}
            </span>
          </button>
        ))}
      </div>

      <SalesTable
        rows={filteredRows}
        slug={slug}
        isCsrOnly={isCsrOnly}
        onEdit={(sale) => { setEditing(sale); setShowForm(true); }}
        onChanged={load}
        canMarkPaid={canImportCsv}
        onMarkPaid={(jn) => setMarkPaidJobs(jn)}
      />
    </div>
  );
}

/* --------------------------------------------------- entry form + ST lookup */
function SaleForm({ slug, user, editing, csrProfiles, isCsrOnly, onClose, onSaved }) {
  const [jobNumber, setJobNumber] = useState(editing?.job_number || "");
  const [lookup, setLookup] = useState(
    editing
      ? {
          found: true,
          job_number: editing.job_number,
          customer_name: editing.customer_name,
          tech_assigned: editing.tech_assigned,
          invoice_date: editing.st_invoice_date,
          invoice_total: editing.st_invoice_total,
          source: editing.st_lookup_source,
        }
      : null,
  );
  const [lookupBusy, setLookupBusy] = useState(false);
  const [csrProfileId, setCsrProfileId] = useState(editing?.csr_profile_id || "");
  // CC-071 (2026-09-25): on EDIT, if the stored `sale_date` is null (common
  // on the 45 csv_backfill rows Kat is fixing today — every backfill row
  // came in with sale_date=null when the spreadsheet had no booking date),
  // start the date input BLANK instead of defaulting to `todayIso()`. The
  // old default caused every silent-fail save to also have quietly stamped
  // today's date if the save had ever gone through — a data-corruption
  // footgun on top of the ReferenceError bug. On NEW sales the live form
  // still requires a date (blocked by validation below), so `todayIso()`
  // stays as the new-sale default. Paired with the PATCH-body branch
  // below that OMITS `sale_date` when this stays blank.
  const [saleDate, setSaleDate] = useState(
    editing ? (editing.sale_date || "") : todayIso(),
  );
  const [saleAmount, setSaleAmount] = useState(
    editing?.sale_amount != null ? String(editing.sale_amount) : "",
  );
  const [amountAutofilled, setAmountAutofilled] = useState(false);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [status, setStatus] = useState(editing?.status || "open_need_to_schedule");
  // CC-071 (2026-09-25): CC-037's soft-warn path referenced `manualCustomer`
  // and `manualTech` in both the JSX (lines ~489/500) and the submit handler
  // (lines ~349/392) but the corresponding `useState` hooks were never added.
  // Result: calling `submit` threw `ReferenceError: manualCustomer is not
  // defined` synchronously before the try/catch, React swallowed the promise
  // rejection, no PATCH ever fired, no error surfaced to the UI — the exact
  // "Save changes does nothing silently" symptom Kat reported. Declaring the
  // hooks here restores the intended behavior; defaults are "" because the
  // manual-entry inputs only render when ST lookup fails on a NEW sale
  // (`lookup && !lookup.found && !editing`), so `editing` prefill is not a
  // path these fields ever hit.
  const [manualCustomer, setManualCustomer] = useState("");
  const [manualTech, setManualTech] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // CC-066 (2026-09-25): key the stage-dropdown lock off matched_run_id
  // (real compute-ownership), not off the status enum. Historical backfill
  // rows carry compute-owned status labels with null matched_run_id and
  // must remain editable so Katt can fix sale_date on them.
  const editingLocked = editing && isRowLocked(editing);
  // CC-062 (2026-09-24) — when the row is compute-owned we show a
  // full banner up top explaining WHY it's locked (compute-owned by
  // the paid payroll run) and which pay week that run covers, so
  // Kat can ask the right admin the right question. Pay-week text
  // is plain, not a clickable link — CSRs don't have permission to
  // open the payroll run and clicking through to a 403 is exactly
  // the "silent failure" pattern CC-061 exists to kill.

  useEffect(() => {
    if (editing || csrProfileId) return;
    const email = (user?.email || "").toLowerCase();
    const match = csrProfiles.find((p) => (p.email || "").toLowerCase() === email);
    if (match) setCsrProfileId(match.id);
  }, [csrProfiles, user, editing, csrProfileId]);

  const doLookup = useCallback(async () => {
    const jn = (jobNumber || "").trim();
    if (!jn) return;
    setLookupBusy(true); setErr(null); setLookup(null);
    try {
      const { data } = await api.get(
        `/csr-sales/st-job-lookup?tenant_slug=${slug}&job_number=${encodeURIComponent(jn)}`,
      );
      setLookup(data);
      if (!data.found) {
        // CC-087 (2026-09-28): only surface the red `err` banner for a
        // genuine input error (malformed job number). A non-malformed
        // {found:false} is the normal state for a brand-new sale the
        // CSR is logging BEFORE the ST commission CSV lands — that's
        // exactly what CC-038's amber "type customer name to save"
        // banner (rendered below in the JSX) is for. Firing a red
        // error alongside the amber banner made Kat see contradictory
        // signals and led her to think the save was blocked; it also
        // coincided with a submit-button disable gate that WAS
        // actually blocking her (see CC-087 fix at the submit button
        // and at line 396 for the silent sale_amount requirement).
        if (data.reason === "malformed") {
          setErr("Job number must be 3-8 digits (numbers only).");
        }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLookupBusy(false);
    }
  }, [jobNumber, slug]);

  useEffect(() => {
    if (editing) return;
    if (!jobNumber || jobNumber.trim().length < 3) { setLookup(null); return; }
    const h = setTimeout(doLookup, 400);
    return () => clearTimeout(h);
  }, [jobNumber, editing, doLookup]);

  // Prefill sale amount from ST lookup when it lands (only if the user
  // hasn't already typed a manual value).
  useEffect(() => {
    if (!lookup?.found) return;
    if (lookup.invoice_total == null) return;
    if (saleAmount && !amountAutofilled) return;  // user typed something
    setSaleAmount(String(lookup.invoice_total));
    setAmountAutofilled(true);
  }, [lookup, saleAmount, amountAutofilled]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    // CC-037: soft-warn path. If ST lookup returned {found:false}, allow
    // save only when the CSR has typed a customer name manually.
    const lookupFailed = lookup && !lookup.found;
    const manualCustomerTrim = (manualCustomer || "").trim();
    if (!lookup && !editing) { setErr("Look up the ServiceTitan job number first."); return; }
    if (lookupFailed && !manualCustomerTrim) {
      setErr("Type the customer name so this sale can save. It will be flagged for verification.");
      return;
    }
    if (!isCsrOnly && !csrProfileId) { setErr("Pick which CSR converted this estimate."); return; }
    // CC-071 (2026-09-25): allow BLANK sale_date on edit — the backfill rows
    // Kat is fixing today were imported with sale_date=null on purpose (no
    // booking date known at import time). Blocking her save because the
    // field is empty would defeat the entire "let her fix rows without
    // stamping today's date" requirement. New sales still require the date
    // (creation contract in the live form has always required it).
    if (saleDate && !/^\d{4}-\d{2}-\d{2}$/.test(saleDate)) {
      setErr("Sale date must be YYYY-MM-DD.");
      return;
    }
    if (!editing && !saleDate) {
      setErr("Sale date is required for a new sale.");
      return;
    }
    const amtNum = Number(saleAmount);
    if (!saleAmount || Number.isNaN(amtNum) || amtNum < 0) {
      setErr("Sale amount is required and must be a non-negative number.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        // CC-071 (2026-09-25): only include `sale_date` in the PATCH when
        // Kat actually filled it in. Omitting the key leaves the stored
        // value untouched — the backend PATCH handler at
        // csr_sales.py:506 `if body.sale_date is not None: ...` treats
        // absent-key as "don't change" (verified). This lets Kat save a
        // stage move / amount fix / notes edit on a null-date backfill
        // row WITHOUT silently stamping today's date onto it.
        const patch = {
          sale_amount: amtNum,
          notes: notes || null,
        };
        if (saleDate) patch.sale_date = saleDate;
        if (!isCsrOnly && csrProfileId) patch.csr_profile_id = csrProfileId;
        // Only include a status change when (a) the current row is not
        // truly compute-owned (unlocked historical rows can be re-staged
        // freely) AND (b) the new status is a manual stage (enforced by
        // the <select> options anyway).
        if (!isRowLocked(editing) && status !== editing.status && MANUAL_STATES.has(status)) {
          patch.status = status;
        }
        await api.patch(`/csr-sales/${editing.id}?tenant_slug=${slug}`, patch);
      } else {
        const payload = {
          job_number: jobNumber.trim(),
          sale_date: saleDate,
          sale_amount: amtNum,
          notes: notes || null,
          status,
        };
        if (!isCsrOnly) payload.csr_profile_id = csrProfileId;
        // CC-037: attach manual customer/tech only when ST lookup failed.
        // Server ignores these fields if lookup succeeded — ST wins.
        if (lookupFailed) {
          payload.customer_name = manualCustomerTrim;
          const t = (manualTech || "").trim();
          if (t) payload.tech_assigned = t;
        }
        await api.post(`/csr-sales?tenant_slug=${slug}`, payload);
      }
      onSaved();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || ex.message));
    } finally {
      setBusy(false);
    }
  };

  const isMock = lookup?.source === "mock_preview";

  return (
    <form onSubmit={submit} className="bg-white border border-[#e7e9ee] rounded-xl p-5 space-y-4" data-testid="csr-sales-form">
      {editingLocked && <LockedSaleBanner sale={editing} />}
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-[#0e1420]">
          {editing ? `Edit sale — job #${editing.job_number}` : "Log a new CSR sale"}
        </div>
        <button type="button" onClick={onClose} className="text-[12px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]" data-testid="csr-sales-form-cancel">
          Cancel
        </button>
      </div>

      <div>
        <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">ServiceTitan job number</label>
        <div className="flex gap-2">
          <input
            type="text"
            data-testid="csr-sales-form-job-number"
            value={jobNumber}
            onChange={(e) => setJobNumber(e.target.value.replace(/[^\d]/g, ""))}
            disabled={!!editing}
            placeholder="e.g. 37166"
            className="flex-1 border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data disabled:bg-[#f9fafb] disabled:opacity-70"
          />
          <button
            type="button"
            onClick={doLookup}
            disabled={lookupBusy || !jobNumber || !!editing}
            data-testid="csr-sales-form-lookup-btn"
            className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f9fafb] disabled:opacity-60"
          >
            {lookupBusy ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />}
            Look up
          </button>
        </div>
        {lookup?.found && (
          <div className={`mt-2 rounded-lg border p-3 text-[12.5px] ${isMock ? "border-[#f0d68b] bg-[#fef8e5]" : "border-[#c6e6d3] bg-[#eef8f2]"}`} data-testid="csr-sales-form-lookup-result">
            <div className="flex items-center gap-1 font-semibold">
              <CheckCircle2 size={13} className={isMock ? "text-[#8a5a1a]" : "text-[#0e6b52]"} />
              {isMock ? "Mocked ST match (preview)" : "ServiceTitan match found"}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-1.5 font-mono-data text-[12px]">
              <div><span className="text-[color:var(--cc-ink-muted)]">Customer: </span>{lookup.customer_name || "—"}</div>
              <div><span className="text-[color:var(--cc-ink-muted)]">Tech: </span>{lookup.tech_assigned || "—"}</div>
              <div><span className="text-[color:var(--cc-ink-muted)]">ST invoice date: </span>{lookup.invoice_date || "—"}</div>
              <div><span className="text-[color:var(--cc-ink-muted)]">ST invoice total: </span>{money(lookup.invoice_total)}</div>
            </div>
          </div>
        )}
        {lookup && !lookup.found && !editing && (
          <div className="mt-2 rounded-lg border border-[#f0b96b] bg-[#fdf1de] p-3 text-[12.5px]" data-testid="csr-sales-form-manual-verify">
            <div className="flex items-center gap-1 font-semibold text-[#8a5a1a]">
              <AlertCircle size={13} />
              Not in ServiceTitan yet — type the customer name to save (marked for verification)
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1">
              This row will show an amber &ldquo;unverified&rdquo; pill until the commission CSV for this job lands. Then it clears automatically.
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="block text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1">Customer name (required)</label>
                <input
                  type="text"
                  data-testid="csr-sales-form-manual-customer"
                  value={manualCustomer}
                  onChange={(e) => setManualCustomer(e.target.value)}
                  placeholder="e.g. Andrew Lefauve"
                  className="w-full border border-[#e7e9ee] rounded-lg px-3 py-1.5 text-[13px] focus:border-[#a35418] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1">Tech assigned (optional)</label>
                <input
                  type="text"
                  data-testid="csr-sales-form-manual-tech"
                  value={manualTech}
                  onChange={(e) => setManualTech(e.target.value)}
                  placeholder="leave blank if not yet assigned"
                  className="w-full border border-[#e7e9ee] rounded-lg px-3 py-1.5 text-[13px] focus:border-[#a35418] focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {isCsrOnly ? (
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">CSR</label>
            <div
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] bg-[#f9fafb] text-[color:var(--cc-ink-muted)]"
              data-testid="csr-sales-form-csr-locked"
              title="CSRs can only log sales under their own profile. Ask a payroll admin to log on behalf of another CSR."
            >
              {user?.email || "You"}
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">CSR</label>
            <select data-testid="csr-sales-form-csr-picker" value={csrProfileId} onChange={(e) => setCsrProfileId(e.target.value)} className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px]">
              <option value="">— pick CSR —</option>
              {csrProfiles.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Date converted</label>
          <input type="date" data-testid="csr-sales-form-sale-date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] font-mono-data" />
        </div>
        <div>
          <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
            Sale amount ($) <span className="text-[#7a1c1c] normal-case tracking-normal">— required</span>
            {amountAutofilled && (
              <span className="ml-1 normal-case tracking-normal text-[10px] text-[#0e6b52] font-normal">· auto-filled from ST</span>
            )}
          </label>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--cc-ink-muted)] text-[13px]">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              data-testid="csr-sales-form-sale-amount"
              value={saleAmount}
              onChange={(e) => { setSaleAmount(e.target.value); setAmountAutofilled(false); }}
              placeholder="Type sale total"
              className="w-full border border-[#e7e9ee] rounded-lg pl-6 pr-3 py-2 text-[13.5px] font-mono-data focus:border-[#0072bc] focus:outline-none"
            />
          </div>
          <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Feeds the 15% / 20.5%-over-$7,500 commission math.
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Stage</label>
        <select
          data-testid="csr-sales-form-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={editingLocked}
          className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] disabled:bg-[#f9fafb] disabled:opacity-70"
        >
          <option value="open_need_to_schedule">1. Open — need to schedule</option>
          <option value="closing_this_week">2. Closing this week</option>
          <option value="closing_next_week">3. Closing next week</option>
        </select>
        {editingLocked && (
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
            Stage is compute-owned once matched to a payroll run. Unapprove the run to re-stage.
          </div>
        )}
        {!editingLocked && editing && !MANUAL_STATES.has(editing.status) && (
          <div className="text-[11px] text-[#8a5a1a] bg-[#fdf6e5] border border-[#f0d999] rounded px-2 py-1.5 mt-1" data-testid="csr-sales-form-historical-note">
            Historical backfill row — not bound to a payroll run. Sale date and amount can be fixed here; stage moves back to a manual stage if you save a change.
          </div>
        )}
      </div>

      <div>
        <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Notes <span className="normal-case tracking-normal text-[color:var(--cc-ink-muted)]">(optional)</span></label>
        <textarea data-testid="csr-sales-form-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px]" />
      </div>

      {err && (
        <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c] flex items-start gap-2" data-testid="csr-sales-form-err">
          <AlertCircle size={14} strokeWidth={1.9} className="mt-0.5" /><div>{err}</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {/*
          CC-087 (2026-09-28): amber-path-aware submit gate. The prior
          gate `!editing && !lookup?.found` disabled the button on ANY
          ST-lookup miss, even when the amber "type customer name to
          save" path was fully satisfied — Kat couldn't log any new
          sale whose ST job wasn't yet in the ST invoice pull, which
          is the entire scenario CC-038 was built for. New rule: on
          new sales, allow submit when either (a) ST lookup succeeded
          OR (b) ST lookup returned {found:false} AND the CSR has
          typed a manual customer name (the amber-path contract).
        */}
        <button
          type="submit"
          disabled={
            busy
            || (!editing
                && !lookup?.found
                && !(lookup && !lookup.found && (manualCustomer || "").trim().length > 0))
          }
          data-testid="csr-sales-form-submit"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a94] disabled:opacity-60"
        >
          <Send size={13} /> {busy ? "Saving…" : (editing ? "Save changes" : "Log sale")}
        </button>
      </div>
    </form>
  );
}

/* --------------------------------------------------- csv import modal */
function ImportModal({ slug, onClose, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const runPreview = async () => {
    setErr(null); setPreview(null); setBusy(true);
    try {
      const { data } = await api.post(
        `/csr-sales/bulk-import-csv?tenant_slug=${slug}`,
        { csv: csvText, dry_run: true },
      );
      setPreview(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || e.message));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post(
        `/csr-sales/bulk-import-csv?tenant_slug=${slug}`,
        { csv: csvText, dry_run: false },
      );
      setPreview(data);
      if (data.inserted_count > 0) onImported();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || e.message));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-auto" data-testid="csr-sales-import-modal" onClick={onClose}>
      <div className="bg-white rounded-xl border border-[#e7e9ee] max-w-[900px] w-full my-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[#eef1f6] px-5 py-3 flex items-center justify-between">
          <div className="font-semibold text-[15px] text-[#0e1420] flex items-center gap-2">
            <Upload size={15} /> Import CSR sales from CSV
          </div>
          <button type="button" onClick={onClose} className="text-[12px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]" data-testid="csr-sales-import-close">Close</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-[#f9fafb] border border-[#eef1f6] rounded-lg p-3 text-[12.5px] text-[color:var(--cc-ink-muted)]">
            <div className="font-semibold text-[#0e1420] mb-1">Expected columns (case- and order-insensitive):</div>
            <code className="font-mono-data text-[11.5px] text-[#0e1420]">
              job_number, sale_date, sale_amount, status, csr_name, customer_name, tech_assigned, matched_pay_week_monday, notes
            </code>
            <div className="mt-2">
              <b>status</b> must be one of:{" "}
              <code className="font-mono-data text-[11.5px]">{STATUS_ORDER.join(", ")}</code>
            </div>
            <div className="mt-1">
              <b>sale_date</b> is optional on import — leave blank for rows where the
              booking date is unknown and backfill later once the ST admin pull lands.
              If provided, it must be <code className="font-mono-data text-[11.5px]">YYYY-MM-DD</code>.
              (The live &ldquo;Log new sale&rdquo; form still requires it.)
            </div>
            <div className="mt-1">Duplicates (same job_number in tenant) are skipped. Historical rows skip ST lookup.</div>
          </div>

          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f9fafb] cursor-pointer" data-testid="csr-sales-import-file-label">
              <Upload size={12} /> Upload .csv file
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csr-sales-import-file-input" />
            </label>
            <div className="text-[11px] text-[color:var(--cc-ink-muted)]">or paste CSV text below</div>
          </div>

          <textarea
            data-testid="csr-sales-import-textarea"
            rows={8}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder="job_number,sale_date,sale_amount,status,csr_name,customer_name,tech_assigned,notes&#10;37166,2026-08-14,8200.00,needs_pay_to_collect,Katt,ACME LLC,Chris Prather,Cash sale&#10;37201,,2450.00,open_need_to_schedule,Juliana,Beta Co,Josh Barron,Historical — no date on hand"
            className="w-full font-mono-data text-[11.5px] border border-[#e7e9ee] rounded-lg px-3 py-2 focus:border-[#0072bc] focus:outline-none"
          />

          <div className="flex items-center gap-2 justify-end">
            <button type="button" onClick={runPreview} disabled={busy || !csvText.trim()} data-testid="csr-sales-import-preview-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f9fafb] disabled:opacity-60">
              {busy ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />} Preview
            </button>
            <button type="button" onClick={commit} disabled={busy || !preview || preview.inserted_count === 0} data-testid="csr-sales-import-commit-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a94] disabled:opacity-60">
              <CheckCircle2 size={13} /> Import {preview?.inserted_count ? `${preview.inserted_count} rows` : ""}
            </button>
          </div>

          {err && (
            <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c] flex items-start gap-2" data-testid="csr-sales-import-err">
              <AlertCircle size={14} strokeWidth={1.9} className="mt-0.5" /><div>{err}</div>
            </div>
          )}

          {preview && (
            <div className="border border-[#eef1f6] rounded-lg p-3 text-[12.5px]" data-testid="csr-sales-import-preview">
              <div className="grid grid-cols-3 gap-3">
                <div><span className="text-[color:var(--cc-ink-muted)]">Insertable rows:</span> <b>{preview.inserted_count}</b></div>
                <div><span className="text-[color:var(--cc-ink-muted)]">Skipped duplicates:</span> <b>{preview.skipped_duplicates_count}</b></div>
                <div><span className="text-[color:var(--cc-ink-muted)]">Errors:</span> <b className={preview.errors_count > 0 ? "text-[#7a1c1c]" : ""}>{preview.errors_count}</b></div>
              </div>
              {preview.dry_run === false && preview.inserted_count > 0 && (
                <div className="mt-2 text-[#0e6b52] font-semibold">✔ Imported {preview.inserted_count} row(s) into CSR sales.</div>
              )}
              {preview.errors_count > 0 && (
                <div className="mt-2 max-h-[160px] overflow-auto border-t border-[#eef1f6] pt-2">
                  <div className="font-semibold text-[11.5px] text-[#7a1c1c] mb-1">Row-level errors:</div>
                  <ul className="space-y-0.5 font-mono-data text-[11px] text-[#7a1c1c]">
                    {preview.errors.slice(0, 50).map((er, i) => (
                      <li key={i} data-testid={`csr-sales-import-error-${er.row_index}`}>
                        row {er.row_index}: {er.message}
                      </li>
                    ))}
                    {preview.errors.length > 50 && <li>+ {preview.errors.length - 50} more…</li>}
                  </ul>
                </div>
              )}
              {preview.skipped_duplicates_count > 0 && (
                <div className="mt-2 text-[11.5px] text-[#8a5a1a]">
                  Skipped duplicates:{" "}
                  <code className="font-mono-data">
                    {preview.skipped_duplicates.map((s) => s.job_number).join(", ")}
                  </code>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------- results table */
function SalesTable({ rows, slug, isCsrOnly, onEdit, onChanged, canMarkPaid, onMarkPaid }) {
  const [deleting, setDeleting] = useState(null);
  const [transitioning, setTransitioning] = useState(null);
  const [err, setErr] = useState(null);

  const del = async (sale) => {
    if (!window.confirm(`Delete CSR sale for job #${sale.job_number}?`)) return;
    setDeleting(sale.id); setErr(null);
    try {
      await api.delete(`/csr-sales/${sale.id}?tenant_slug=${slug}`);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || e.message));
    } finally {
      setDeleting(null);
    }
  };

  const transitionTo = async (sale, newStatus) => {
    setTransitioning(sale.id); setErr(null);
    try {
      await api.patch(`/csr-sales/${sale.id}?tenant_slug=${slug}`, { status: newStatus });
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || e.message));
    } finally {
      setTransitioning(null);
    }
  };

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-[#e7e9ee] bg-[#fafbfd] px-6 py-10 text-center text-[13px] text-[color:var(--cc-ink-muted)]" data-testid="csr-sales-empty">
        <Sparkles size={20} className="mx-auto mb-2 text-[color:var(--cc-ink-muted)]" />
        No sales in this bucket yet.
      </div>
    );
  }

  return (
    <div className="border border-[#e7e9ee] rounded-xl overflow-hidden bg-white">
      {err && (
        <div className="bg-[#fdecec] border-b border-[#f7c1c1] px-3 py-2 text-[12.5px] text-[#7a1c1c] flex items-start gap-2" data-testid="csr-sales-delete-err">
          <AlertCircle size={14} strokeWidth={1.9} className="mt-0.5" /> {err}
        </div>
      )}
      <table className="w-full text-[12.5px]" data-testid="csr-sales-table">
        <thead className="bg-[#f9fafb] text-[10.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className="text-left py-2 px-3">Job #</th>
            <th className="text-left py-2 px-3">Customer</th>
            <th className="text-left py-2 px-3">Tech assigned</th>
            <th className="text-left py-2 px-3">CSR</th>
            <th className="text-left py-2 px-3">Sale date</th>
            <th className="text-right py-2 px-3">Amount</th>
            <th className="text-left py-2 px-3">Stage</th>
            <th className="text-left py-2 px-3">Matched pay week</th>
            <th className="text-right py-2 px-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // CC-066: `canManualMove` means the inline stage-change dropdown
            // is shown. Keep it gated on MANUAL_STATES since the dropdown
            // only offers manual stages — a stage-6 unmatched historical
            // row still shows the "locked" pill for stage moves, but its
            // Edit button now opens so Katt can fix sale_date and amount.
            const canManualMove = MANUAL_STATES.has(r.status);
            const rowLocked = isRowLocked(r);
            const isComputeOwned = rowLocked;
            // CC-066: Edit access — allow any row that isn't bound to a
            // real payroll run, regardless of the status label. Stage
            // change inside the form is still gated by `editingLocked`
            // (matched_run_id), which now correctly stays off for
            // historical rows.
            const canOpenEditor = !rowLocked;
            return (
              <tr key={r.id} className="border-t border-[#eef1f6]" data-testid={`csr-sales-row-${r.job_number}`}>
                <td className="py-2 px-3 font-mono-data font-bold text-[#0e1420]">{r.job_number}</td>
                <td className="py-2 px-3">
                  {r.customer_name || (
                    <span className="text-[color:var(--cc-ink-muted)] italic">—</span>
                  )}
                  {r.needs_manual_verification && (
                    <span
                      data-testid={`csr-sales-unverified-pill-${r.job_number}`}
                      title="Not yet matched to ServiceTitan. Customer name was typed manually; will auto-clear when the commission CSV for this job lands."
                      className="ml-2 inline-block px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide rounded bg-[#fdf1de] text-[#8a5a1a] border border-[#f0b96b]"
                    >
                      ⚠ Unverified
                    </span>
                  )}
                </td>
                <td className="py-2 px-3">{r.tech_assigned || <span className="text-[color:var(--cc-ink-muted)] italic">—</span>}</td>
                <td className="py-2 px-3">{r.csr_name || "—"}</td>
                <td className="py-2 px-3 font-mono-data" data-testid={`csr-sales-date-${r.job_number}`}>
                  {r.sale_date || <span className="text-[color:var(--cc-ink-muted)] italic">—</span>}
                </td>
                <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#0e1420]" data-testid={`csr-sales-amount-${r.job_number}`}>
                  {money(r.sale_amount)}
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${STATUS_STYLE[r.status]}`}>
                      {STATUS_SHORT[r.status]}
                    </span>
                    {canManualMove && (
                      <select
                        value={r.status}
                        onChange={(e) => transitionTo(r, e.target.value)}
                        disabled={transitioning === r.id}
                        data-testid={`csr-sales-move-${r.job_number}`}
                        className="text-[10.5px] border border-[#e7e9ee] rounded px-1 py-0.5 bg-white hover:border-[#0072bc]"
                        title="Move to another manual stage"
                      >
                        <option value="open_need_to_schedule">→ Open</option>
                        <option value="closing_this_week">→ Closing this wk</option>
                        <option value="closing_next_week">→ Closing next wk</option>
                      </select>
                    )}
                    <PaidBadge r={r} />
                    {(r.reversal_history || []).length > 0 && (
                      <span title={`Reverted ${r.reversal_history.length} time(s)`}
                        className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-[#fdecec] text-[#7a1c1c]">
                        ↺ {r.reversal_history.length}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-3 font-mono-data text-[color:var(--cc-ink-muted)]">{r.matched_pay_week_monday || "—"}</td>
                <td className="py-2 px-3 text-right">
                  {canMarkPaid && (r.paid_outside ? (
                    <button type="button" data-testid={`csr-sales-undo-paid-${r.job_number}`}
                      onClick={async () => { try { if (await undoPaid(slug, r.job_number)) onChanged(); } catch (e) { window.alert(e?.response?.data?.detail || e.message); } }}
                      className="text-[11.5px] font-semibold text-[#8a5a1a] hover:underline mr-3">Undo paid</button>
                  ) : (
                    <button type="button" data-testid={`csr-sales-mark-paid-${r.job_number}`} onClick={() => onMarkPaid(r.job_number)}
                      className="text-[11.5px] font-semibold text-[#14532d] hover:underline mr-3">Mark paid</button>
                  ))}
                  {(!isCsrOnly || r._current_user_owns) ? (
                    <>
                      {canOpenEditor && (
                        <button type="button" onClick={() => onEdit(r)} data-testid={`csr-sales-edit-${r.job_number}`}
                          className="text-[11.5px] font-semibold text-[#005a94] hover:underline mr-3">Edit</button>
                      )}
                      {rowLocked && (
                        <span data-testid={`csr-sales-edit-locked-${r.job_number}`}
                          className="text-[11px] text-[color:var(--cc-ink-muted)] italic mr-3"
                          title="This sale is bound to an approved payroll run. Ask a payroll admin to unapprove that run before editing.">
                          locked
                        </span>
                      )}
                      <button type="button" onClick={() => del(r)}
                        disabled={deleting === r.id || rowLocked}
                        data-testid={`csr-sales-delete-${r.job_number}`}
                        title={rowLocked ? "Cannot delete a sale on an approved payroll run" : "Delete this entry"}
                        className="text-[11.5px] font-semibold text-[#7a1c1c] hover:underline disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
                        <Trash2 size={11} /> {deleting === r.id ? "…" : "Delete"}
                      </button>
                    </>
                  ) : (
                    <span
                      className="text-[10.5px] text-[color:var(--cc-ink-muted)] italic"
                      title="Only the CSR who logged this sale can edit it. Ask a payroll admin to change it."
                      data-testid={`csr-sales-readonly-${r.job_number}`}
                    >
                      not yours
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
